import { useState, type FormEvent } from 'react'
import { ApiClientError, Table, useApiQuery, useToast, type Column } from 'grabbitbot-legacy-ui'
import { wnfClient } from '../../api'
import type { ServiceAccount } from '../../api/types'
import { PageState } from '../../components/PageState'
import { describeLoadError } from '../../lib/errors'
import { berlinDateTime } from '../../lib/format'

interface Shown {
  login: string
  secret: string
  fresh: boolean
}

/** Legacy `ServiceAccountListComponent` — the bot credential. The bot
 * authenticates with HTTP Basic `<login>:<secret>`; the login and the secret
 * are generated by the backend. The secret is shown once at creation and can
 * be viewed again with "Reveal secret" (owner decision 23d7e03d: keep
 * "view password" now, fix in round 2). */
export function ServiceAccountsPage() {
  const { data, loading, error, refetch } = useApiQuery(() => wnfClient.searchServiceAccounts(), [])
  const roles = useApiQuery(() => wnfClient.searchServiceAccountRoles(), [])
  const { showToast } = useToast()
  const [description, setDescription] = useState('')
  const [comment, setComment] = useState('')
  const [creating, setCreating] = useState(false)
  const [shown, setShown] = useState<Record<string, Shown>>({})
  const [confirming, setConfirming] = useState<string | null>(null)

  async function handleCreate(event: FormEvent) {
    event.preventDefault()
    setCreating(true)
    try {
      const created = await wnfClient.addServiceAccount({
        description: description.trim() || undefined,
        comment: comment.trim() || undefined,
      })
      if (created.secret) {
        setShown((prev) => ({ ...prev, [created.id]: { login: created.login, secret: created.secret!, fresh: true } }))
      }
      setDescription('')
      setComment('')
      refetch()
      showToast('Service account created — copy the secret now', 'success')
    } catch {
      showToast('Could not create the service account', 'error')
    } finally {
      setCreating(false)
    }
  }

  async function reveal(row: ServiceAccount) {
    try {
      const secret = await wnfClient.viewServiceAccountSecret(row.id)
      setShown((prev) => ({ ...prev, [row.id]: { login: row.login, secret, fresh: false } }))
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 410) {
        showToast('This secret can no longer be shown — revoke the account and create a new one.', 'error')
      } else {
        showToast('Could not load the secret.', 'error')
      }
    }
  }

  function hide(id: string) {
    setShown((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  async function revoke(id: string) {
    try {
      await wnfClient.revokeServiceAccount(id)
      hide(id)
      showToast('Service account revoked', 'success')
    } catch {
      showToast('Could not revoke the service account', 'error')
    } finally {
      setConfirming(null)
      refetch()
    }
  }

  const columns: Column<ServiceAccount>[] = [
    { key: 'login', label: 'Login', align: 'left', format: (v) => <code>{String(v)}</code> },
    { key: 'description', label: 'Description', align: 'left' },
    { key: 'roles', label: 'Roles', align: 'left', format: (v) => (Array.isArray(v) ? v.join(', ') : '') },
    {
      key: 'status',
      label: 'Status',
      align: 'left',
      numeric: true,
      format: (v) =>
        v === 0 ? (
          <span className="wnf-badge wnf-badge--ok">Active</span>
        ) : (
          <span className="wnf-badge wnf-badge--off">Revoked</span>
        ),
    },
    { key: 'createdAt', label: 'Created', align: 'left', format: (v) => berlinDateTime(v as string | null) },
    {
      key: 'id',
      label: 'Secret',
      sortable: false,
      align: 'left',
      format: (_v, row) => {
        if (row.status !== 0) return null
        const s = shown[row.id]
        return s ? (
          <span className="wnf-actions">
            <code data-testid={`secret-${row.id}`}>{s.secret}</code>
            <button type="button" onClick={() => hide(row.id)}>
              Hide
            </button>
          </span>
        ) : (
          <button type="button" onClick={() => void reveal(row)} aria-label={`Reveal secret of ${row.login}`}>
            Reveal secret
          </button>
        )
      },
    },
    {
      key: 'revokedAt',
      label: '',
      sortable: false,
      align: 'left',
      format: (_v, row) => {
        if (row.status !== 0) return null
        return confirming === row.id ? (
          <span className="wnf-actions">
            <button type="button" onClick={() => void revoke(row.id)}>
              Confirm revoke
            </button>
            <button type="button" onClick={() => setConfirming(null)}>
              Keep
            </button>
          </span>
        ) : (
          <button type="button" onClick={() => setConfirming(row.id)} aria-label={`Revoke ${row.login}`}>
            Revoke
          </button>
        )
      },
    },
  ]

  const freshOnes = Object.entries(shown).filter(([, s]) => s.fresh)

  return (
    <div>
      <div className="wnf-page-title">
        <h1>Service accounts</h1>
      </div>
      <p className="wnf-muted">
        A service account is the bot&apos;s WnF Exchange key: the bot signs in with HTTP Basic <code>login:secret</code>. It
        only ever sees its own owner&apos;s wallets and orders (test funds).
      </p>

      {freshOnes.map(([id, s]) => (
        <div key={id} className="wnf-alert wnf-alert--info" role="status" data-testid="new-secret">
          <strong>New service account created.</strong> Login <code>{s.login}</code> — secret{' '}
          <code data-testid="new-secret-value">{s.secret}</code>. Copy it into the bot&apos;s configuration now; you can view
          it again later with &quot;Reveal secret&quot;.
        </div>
      ))}

      <form className="wnf-card wnf-form" onSubmit={handleCreate} aria-label="New service account">
        <h2>New service account</h2>
        <label>
          Description
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. spotbot test run" />
        </label>
        <label>
          Comment
          <input value={comment} onChange={(e) => setComment(e.target.value)} />
        </label>
        <p className="wnf-muted">Roles: {roles.data ? roles.data.join(', ') : 'all'} (login and secret are generated for you)</p>
        <button type="submit" disabled={creating}>
          {creating ? 'Creating…' : 'Create'}
        </button>
      </form>

      <PageState loading={loading} error={error ? describeLoadError(error, 'service accounts') : null}>
        {data && data.length === 0 && <p className="wnf-muted">No service accounts yet.</p>}
        {data && data.length > 0 && (
          <Table columns={columns} rows={data} rowKey={(r) => r.id} initialSort={{ key: 'createdAt', dir: 'desc' }} />
        )}
      </PageState>
    </div>
  )
}
